// P-AUTH-GATE-AND-ONBOARDING-DRAFT — the recoverable surface a RETURNING
// provisioned parent sees when their initial cloud students-fetch FAILED
// (timeout / offline / RLS). It replaces the wrong "re-onboard them" fall-
// through (QA-AUDIT Area 1 P1). The parent's kids exist in the cloud; we
// just couldn't read them this boot — so the only honest action is "try
// again," NOT "set up your child from scratch."
//
// Honors PR #78's silent-failure rule: the failure is VISIBLE here (not a
// swallowed empty list that silently routes to onboarding), and a Sentry
// breadcrumb records that this session went through the hold path.
//
// Self-contained inline styles (COLORS tokens) to match the other gate
// screens (Loading/PathSplash) — this renders OUTSIDE the .sb-root scope, so
// it can't rely on the storybook Tailwind preflight.
//
// MA-271: reused for a SECOND hold class via `context="allowlist"` — a
// previously-confirmed parent hitting an allowlist-check outage (see
// allowlistOutageGuard.js). Same shell/retry mechanics, different copy: the
// default "your child's profile is saved in the cloud" copy would be
// factually wrong at the allowlist gate (no child profile is at stake
// there), and this variant adds a "Request access" escape hatch mirroring
// PrivateBetaScreen's, since a revoked/never-approved account could
// coincidentally hit this same outage and needs a way out that isn't
// silently stuck on "Try again" (see MA-271's decision log).
//
// MA-394: a THIRD hold class via `context="zero-active"` — a returning
// parent whose students fetch SUCCEEDED but returned zero active rows (no
// fetch error at all, so the "cloud" copy's "we just couldn't reach it"
// would be factually wrong). "Try again" is honest but can be a dead end for
// the exact incident this class follows (a retention-purge deactivation,
// MA-392/393) — the fetch will keep succeeding-empty until the rows are
// reactivated, which is why this variant surfaces the support contact
// alongside retry rather than leaving retry as the only affordance.

import { useEffect, useState } from "react";
import { COLORS } from "../styles/theme.js";
import { captureAuthGateHold } from "../lib/sentry.ts";

const COPY = {
  cloud: {
    title: "We couldn't load your progress",
    body: "Your child's profile is saved safely in the cloud — we just couldn't reach it. Check your connection and try again.",
  },
  allowlist: {
    title: "We couldn't check your access",
    body: "Your account was already approved for early access — we just couldn't confirm it right now. Check your connection and try again.",
  },
  "zero-active": {
    title: "We couldn't find your child's profile",
    body: "Your account looks right, but we didn't find an active profile this time. Try again, or contact us and we'll help you sort it out.",
  },
};

/**
 * @param {object} props
 * @param {() => Promise<unknown>} props.onRetry - refreshStudents (cloud
 *   context) or the allowlist recheck (allowlist context). On success it
 *   clears the underlying error, so the gate stops holding and this surface
 *   unmounts.
 * @param {Error|null} [props.error] - the captured error (for the breadcrumb
 *   trail; never shown raw to the parent).
 * @param {() => void} [props.onSignOut] - optional escape hatch so the parent
 *   isn't trapped if they're signed into the wrong account.
 * @param {"cloud"|"allowlist"} [props.context="cloud"] - which hold class
 *   this instance represents; only changes copy/escape-hatch, never the
 *   retry mechanics.
 */
export default function CloudErrorRetry({ onRetry, error, onSignOut, context = "cloud" }) {
  const [retrying, setRetrying] = useState(false);
  const copy = COPY[context] ?? COPY.cloud;

  // Breadcrumb once on mount — this session reached the hold path. The
  // underlying error is already captured upstream (useCloudSync / useAllowlist),
  // so this is a breadcrumb, not a second exception.
  useEffect(() => {
    captureAuthGateHold({ message: error?.message ?? "unknown", surface: context });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleRetry = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      await onRetry?.();
    } finally {
      // If the retry succeeded, cloudError clears and the parent gate
      // unmounts this component before we get here; if it failed, re-enable
      // the button so they can try again.
      setRetrying(false);
    }
  };

  return (
    <div
      data-testid="cloud-error-retry"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 20,
        minHeight: "100dvh",
        padding: 24,
        background: COLORS.bg,
        fontFamily: "inherit",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 44, lineHeight: 1 }} aria-hidden="true">📡</div>
      <h1
        style={{
          margin: 0,
          fontSize: 24,
          fontWeight: 900,
          color: COLORS.text,
          maxWidth: 360,
        }}
      >
        {copy.title}
      </h1>
      <p
        style={{
          margin: 0,
          fontSize: 16,
          fontWeight: 600,
          color: COLORS.textSoft,
          maxWidth: 340,
        }}
      >
        {copy.body}
      </p>
      <button
        type="button"
        data-testid="cloud-error-retry-button"
        onClick={handleRetry}
        disabled={retrying}
        style={{
          marginTop: 8,
          padding: "14px 32px",
          fontSize: 18,
          fontWeight: 800,
          fontFamily: "inherit",
          color: "#fff",
          background: COLORS.sky,
          border: "none",
          borderRadius: 999,
          cursor: retrying ? "default" : "pointer",
          opacity: retrying ? 0.6 : 1,
          boxShadow: "0 4px 14px rgba(78,168,255,0.35)",
        }}
      >
        {retrying ? "Trying…" : "Try again"}
      </button>
      {context === "allowlist" && (
        // Escape hatch the "cloud" variant doesn't need: a revoked or
        // never-approved account could coincidentally hit this same outage
        // (see allowlistOutageGuard.js's accepted stale-flag residue), and
        // must not be stuck on "Try again" with no path to actually request
        // access — mirrors PrivateBetaScreen's identical CTA.
        <a
          href="mailto:support@pencilwise.app?subject=Beta%20access%20request"
          data-testid="cloud-error-request-access"
          style={{
            marginTop: 4,
            padding: "8px 16px",
            fontSize: 14,
            fontWeight: 700,
            fontFamily: "inherit",
            color: COLORS.muted,
            textDecoration: "underline",
          }}
        >
          Request access instead
        </a>
      )}
      {context === "zero-active" && (
        // MA-394: "Try again" alone can be a dead end for this class (the
        // fetch will keep succeeding-empty until the rows are reactivated —
        // see the header comment), so this variant surfaces a concrete way to
        // reach support instead of leaving the parent with only a button that
        // may never resolve anything.
        <a
          href="mailto:support@pencilwise.app?subject=Missing%20child%20profile"
          data-testid="cloud-error-contact-support"
          style={{
            marginTop: 4,
            padding: "8px 16px",
            fontSize: 14,
            fontWeight: 700,
            fontFamily: "inherit",
            color: COLORS.muted,
            textDecoration: "underline",
          }}
        >
          Contact support
        </a>
      )}
      {typeof onSignOut === "function" && (
        <button
          type="button"
          data-testid="cloud-error-signout-button"
          onClick={onSignOut}
          style={{
            marginTop: 4,
            padding: "8px 16px",
            fontSize: 14,
            fontWeight: 700,
            fontFamily: "inherit",
            color: COLORS.muted,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          Sign in with a different account
        </button>
      )}
    </div>
  );
}
